import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { AnimatedShinyText } from "@/components/ui/magicui/animated-shiny-text";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getAssistantImageFromStorage } from "@/lib";
import { getGradientBackground } from "@/lib/constants/gradients";
import { cn } from "@/lib/utils";
import useAssistantStore from "@/store/assistantStore";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Bot, Edit, Loader2, Trash2, X } from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { AssistantForm } from "@/components/forms/AssistantForm";
import { AssistantSearchDialog } from "./AssistantSearchDialog";
import AssistantCreationFlow from "./AssistantCreationFlow";

export const AssistantAvatar = ({ assistant, className = "h-5 w-5" }) => {
  const [avatarUrl, setAvatarUrl] = useState(
    assistant?.avatar_url || assistant?.image_url || null
  );
  const [imageError, setImageError] = useState(false);

  // Get the gradient background for this assistant
  const gradientBackground = getGradientBackground(assistant);

  // Reset state when assistant changes
  useEffect(() => {
    setAvatarUrl(assistant?.avatar_url || assistant?.image_url || null);
    setImageError(false);
  }, [assistant]);

  useEffect(() => {
    const refreshImageUrl = async () => {
      if (assistant?.image_path && (!avatarUrl || imageError)) {
        try {
          const freshUrl = await getAssistantImageFromStorage(
            assistant.image_path
          );
          if (freshUrl) {
            setAvatarUrl(freshUrl);
            setImageError(false);
          }
        } catch (error) {
          console.error("Error refreshing selector assistant image:", error);
          const fallbackUrl = assistant?.avatar_url || assistant?.image_url;
          if (fallbackUrl && fallbackUrl !== avatarUrl) {
            setAvatarUrl(fallbackUrl);
          } else {
            setImageError(true);
          }
        }
      }
    };

    refreshImageUrl();
  }, [assistant, avatarUrl, imageError]);

  const handleImageError = () => {
    setImageError(true);
    if (avatarUrl === assistant?.avatar_url && assistant?.image_url) {
      setAvatarUrl(assistant.image_url);
    } else if (avatarUrl === assistant?.image_url && assistant?.avatar_url) {
      setAvatarUrl(assistant.avatar_url);
    }
  };

  if (!assistant || (!avatarUrl && imageError)) {
    return <Bot className="h-4 w-4" />;
  }

  return (
    <Avatar
      className={cn(
        className,
        "rounded-full border border-black/60 dark:border-white/60"
      )}
    >
      <AvatarImage
        src={avatarUrl}
        alt={assistant?.name || "Assistant"}
        onError={handleImageError}
        className="rounded-full"
      />
      <AvatarFallback
        className={`text-xs text-white rounded-full ${gradientBackground}`}
      >
        {assistant?.name?.[0] || "A"}
      </AvatarFallback>
    </Avatar>
  );
};

AssistantAvatar.propTypes = {
  assistant: PropTypes.shape({
    avatar_url: PropTypes.string,
    image_url: PropTypes.string,
    image_path: PropTypes.string,
    name: PropTypes.string,
  }),
  className: PropTypes.string,
};

export function AssistantSelector({
  className,
  onAssistantSelect,
  compact = false,
  hideText = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, _setSearch] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assistantToDelete, setAssistantToDelete] = useState(null);
  const [generationTimeout, setGenerationTimeout] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  // Inline edit mode state
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState(null);
  const [inlineIsSaving, setInlineIsSaving] = useState(false);
  const [_inlineHasUnsavedChanges, setInlineHasUnsavedChanges] =
    useState(false);
  const [inlineCanSave, setInlineCanSave] = useState(false);
  const [inlineSaveCallback, setInlineSaveCallback] = useState(null);
  // Inline creation mode state
  const [isInlineCreating, setIsInlineCreating] = useState(false);
  const [creatingAssistant, setCreatingAssistant] = useState(null);
  // Streaming state for assistant creation
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingFields, setStreamingFields] = useState(null);
  const {
    assistants,
    currentAssistant,
    createAssistantWithConfig,
    setCurrentAssistant,
    setActiveTab: _setActiveTab,
    deleteAssistant,
    error,
    initializing,
    initializeAssistants,
  } = useAssistantStore();

  // Add timeout effect for "Generating..." state
  useEffect(() => {
    const hasNoAssistants = !assistants || assistants.length === 0;
    const hasNoCurrent = !currentAssistant;
    const isStuck = hasNoAssistants && hasNoCurrent && !initializing && !error;

    if (isStuck) {
      // Set timeout after 30 seconds
      const timeoutId = setTimeout(() => {
        setGenerationTimeout(true);
      }, 30000);

      return () => clearTimeout(timeoutId);
    } else {
      setGenerationTimeout(false);
    }
  }, [assistants, currentAssistant, initializing, error]);

  const filteredAssistants =
    assistants?.filter((assistant) =>
      assistant?.name?.toLowerCase().includes(search.toLowerCase())
    ) || [];

  const handleSelect = useCallback(
    async (assistantId) => {
      if (assistantId === "add-new") {
        // Show inline creation mode
        if (isCreating) return;
        setIsCreating(true);

        try {
          // Get personal workspace ID from auth store
          const { useAuthStore } = await import("@/store/authStore");
          const authStore = useAuthStore.getState();
          const personalWorkspaceId = authStore.getPersonalWorkspaceId();

          const newAssistant = await createAssistantWithConfig({
            name: "Untitled Assistant",
            description: "A helpful AI assistant",
            model: "gpt-4.1-mini",
            instructions: "You are a helpful AI assistant.",
            tools: [],
            workspace_id: personalWorkspaceId,
            metadata: {
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              is_default: "false",
              inline_creation: "true",
            },
            is_default: false,
          });

          if (!newAssistant.id) {
            throw new Error("Failed to get proper assistant ID");
          }

          // Set the new assistant as current and show inline creation
          setCurrentAssistant(newAssistant);
          setCreatingAssistant(newAssistant);
          setIsInlineCreating(true);

          if (onAssistantSelect) {
            onAssistantSelect(newAssistant);
          }
        } catch (error) {
          console.error("Failed to create assistant:", error);
        } finally {
          setIsCreating(false);
        }
        return;
      }

      try {
        const selectedAssistant = assistants.find(
          (assistant) => assistant.id === assistantId
        );

        if (!selectedAssistant) {
          throw new Error(`Assistant with ID ${assistantId} not found`);
        }

        setCurrentAssistant(selectedAssistant);
        setOpen(false);

        if (onAssistantSelect) {
          onAssistantSelect(selectedAssistant);
        }
      } catch (error) {
        console.error("Failed to switch assistant:", error);
      }
    },
    [
      assistants,
      setCurrentAssistant,
      setOpen,
      createAssistantWithConfig,
      isCreating,
      onAssistantSelect,
    ]
  );

  const handleDelete = async (e, assistantId) => {
    e.preventDefault();
    e.stopPropagation();

    if (currentAssistant?.id === assistantId) {
      // Show a user-friendly alert for trying to delete the active assistant
      alert(
        "Cannot delete the currently active assistant. Please switch to another assistant first."
      );
      return;
    }

    // Show confirmation dialog instead of direct deletion
    const assistant = assistants.find((a) => a.id === assistantId);
    setAssistantToDelete(assistant);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!assistantToDelete) return;

    try {
      // If we're deleting the current assistant, set the next one first
      if (currentAssistant?.id === assistantToDelete.id) {
        const remainingAssistants = assistants.filter(
          (a) => a.id !== assistantToDelete.id
        );
        if (remainingAssistants.length > 0) {
          // Set the first remaining assistant as current
          const nextAssistant = remainingAssistants[0];
          setCurrentAssistant(nextAssistant);
          if (onAssistantSelect) {
            onAssistantSelect(nextAssistant);
          }
        }
      }

      await deleteAssistant(assistantToDelete.id);
      setDeleteConfirmOpen(false);
      setAssistantToDelete(null);
    } catch (error) {
      console.error("Failed to delete assistant:", error);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setAssistantToDelete(null);
  };

  const handleEdit = (e, assistantId) => {
    e.preventDefault();
    e.stopPropagation();

    const selectedAssistant = assistants.find(
      (assistant) => assistant.id === assistantId
    );

    if (selectedAssistant) {
      setCurrentAssistant(selectedAssistant);
      setEditingAssistant(selectedAssistant);
      setIsInlineEditing(true);
    }
  };

  // Inline edit mode handlers
  const handleCloseInlineEdit = () => {
    setIsInlineEditing(false);
    setEditingAssistant(null);
    setInlineIsSaving(false);
    setInlineHasUnsavedChanges(false);
    setInlineCanSave(false);
    setInlineSaveCallback(null);
  };

  const handleSaveInlineEdit = async () => {
    if (inlineSaveCallback) {
      await inlineSaveCallback();
      setTimeout(() => {
        handleCloseInlineEdit();
      }, 1000);
    }
  };

  // Inline creation mode handlers
  const handleCloseInlineCreate = useCallback(async () => {
    // If we have a creating assistant that hasn't been properly configured,
    // delete it to avoid orphaned "Untitled Assistant" entries
    if (
      creatingAssistant?.id &&
      creatingAssistant?.metadata?.inline_creation === "true"
    ) {
      try {
        // Check if the assistant still has the default unconfigured state
        const isUnconfigured =
          creatingAssistant.name === "Untitled Assistant" ||
          !creatingAssistant.metadata?.generated_from_prompt;

        if (isUnconfigured) {
          // Switch to another assistant first so UI isn't left in a broken state
          const remainingAssistants = assistants.filter(
            (a) => a.id !== creatingAssistant.id
          );
          if (remainingAssistants.length > 0) {
            setCurrentAssistant(remainingAssistants[0]);
          }

          try {
            await deleteAssistant(creatingAssistant.id);
          } catch {
            // Deletion may fail if the assistant is the last one or already deleted.
            // The orphan cleanup on next app init will catch these.
          }
        }
      } catch (error) {
        console.error("Failed to clean up unconfigured assistant:", error);
      }
    }

    setIsInlineCreating(false);
    setCreatingAssistant(null);
    setInlineIsSaving(false);
    setInlineHasUnsavedChanges(false);
    setInlineCanSave(false);
    setInlineSaveCallback(null);
  }, [creatingAssistant, deleteAssistant, assistants, setCurrentAssistant]);

  // Handle streaming updates during assistant creation
  const handleStreamingUpdate = useCallback(
    (update) => {
      if (update.type === "switch_to_edit") {
        // Switch from creation mode to editing mode when name is ready
        setIsStreaming(true);
        setStreamingFields({
          name: update.name,
          description: "",
          instructions: "",
        });

        // Create a temporary assistant object for the form with the streamed name
        const streamingAssistant = {
          ...creatingAssistant,
          name: update.name,
          description: "",
          instructions: "",
          metadata: {
            ...creatingAssistant?.metadata,
            generation_prompt: update.prompt,
            generated_from_prompt: "true",
          },
        };

        // Switch to edit mode
        setIsInlineCreating(false);
        setEditingAssistant(streamingAssistant);
        setIsInlineEditing(true);
      } else if (update.type === "field_update") {
        // Update the streaming fields
        setStreamingFields(update.fields);

        // Update the editing assistant with streamed values
        if (editingAssistant) {
          setEditingAssistant((prev) => ({
            ...prev,
            name: update.fields.name || prev.name,
            description: update.fields.description || prev.description,
            instructions: update.fields.instructions || prev.instructions,
          }));
        }

        // Mark streaming complete when done
        if (update.isComplete) {
          setIsStreaming(false);
        }
      }
    },
    [creatingAssistant, editingAssistant]
  );

  // Handle popover open/close - cleanup inline creating state when closing
  const handleOpenChange = useCallback(
    (newOpen) => {
      if (!newOpen && isInlineCreating) {
        handleCloseInlineCreate();
      }
      if (!newOpen) {
        // Reset streaming state when popover closes
        setIsStreaming(false);
        setStreamingFields(null);
      }
      setOpen(newOpen);
    },
    [isInlineCreating, handleCloseInlineCreate]
  );

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid="assistant-selector"
            className={cn(
              "justify-between",
              compact ? "h-7 text-xs px-1 ml-1 sm:ml-0 min-w-0" : "w-full",
              "bg-transparent",
              "border border-transparent",
              "hover:bg-transparent",
              "transition-[background-color,opacity] duration-200",
              "focus:outline-none",
              "focus-visible:outline-none",
              "focus:ring-0",
              "focus-visible:ring-0",
              "focus:ring-transparent",
              "focus-visible:ring-transparent",
              "focus:ring-offset-0",
              "focus-visible:ring-offset-0",
              "!ring-offset-transparent",
              "focus:border-transparent",
              "focus-visible:border-transparent",
              "data-[state=open]:ring-0",
              "data-[state=open]:ring-transparent",
              "data-[state=open]:ring-offset-0",
              "data-[state=open]:border-transparent",
              "data-[state=closed]:ring-0",
              "data-[state=closed]:ring-transparent",
              "data-[state=closed]:ring-offset-0",
              "data-[state=closed]:border-transparent",
              className
            )}
          >
            <div className="flex items-center gap-2 w-full justify-end group">
              {/* Show error state when there's a connection error */}
              {error &&
              (!assistants || assistants.length === 0) &&
              !currentAssistant ? (
                <div className="flex items-center gap-2 flex-grow justify-end">
                  <span
                    className={cn(
                      "text-amber-600/80 dark:text-amber-400/80 font-medium",
                      compact ? "text-xs" : "text-sm"
                    )}
                  >
                    Connection error
                  </span>
                  <motion.div
                    initial={false}
                    animate={{
                      rotate: open ? 360 : 0,
                      scale: open ? 1.1 : 1,
                    }}
                    className="text-amber-600/80 dark:text-amber-400/80 flex-shrink-0"
                  >
                    <Bot className={compact ? "h-4 w-4" : "h-5 w-5"} />
                  </motion.div>
                </div>
              ) : /* Show timeout state if generation is stuck */
              generationTimeout &&
                (!assistants || assistants.length === 0) &&
                !currentAssistant ? (
                <div className="flex items-center gap-2 flex-grow justify-end">
                  <span
                    className={cn(
                      "text-red-500/80 dark:text-red-400/80 font-medium",
                      compact ? "text-xs" : "text-sm"
                    )}
                  >
                    Setup incomplete
                  </span>
                  <motion.div
                    initial={false}
                    animate={{
                      rotate: open ? 360 : 0,
                      scale: open ? 1.1 : 1,
                    }}
                    className="text-red-500/80 dark:text-red-400/80 flex-shrink-0"
                  >
                    <Bot className={compact ? "h-4 w-4" : "h-5 w-5"} />
                  </motion.div>
                </div>
              ) : /* Conditionally render AnimatedShinyText when no assistants available */
              (!assistants || assistants.length === 0) && !currentAssistant ? (
                <AnimatedShinyText
                  className={cn(
                    "text-purple-600/80 dark:text-purple-400/80 font-medium flex items-center gap-2 flex-grow justify-end",
                    compact ? "text-xs" : "text-sm"
                  )}
                  shimmerWidth={120}
                >
                  <span className={cn("mr-4", compact ? "mr-2" : "mr-6")}>
                    Generating...
                  </span>
                  <motion.div
                    initial={false}
                    animate={{
                      rotate: open ? 360 : 0,
                      scale: open ? 1.1 : 1,
                    }}
                    className="text-purple-600/80 dark:text-purple-400/80 flex-shrink-0"
                  >
                    <Bot className={compact ? "h-4 w-4" : "h-5 w-5"} />
                  </motion.div>
                </AnimatedShinyText>
              ) : (
                <>
                  <span
                    className={cn(
                      "truncate text-foreground/90 text-right font-medium",
                      compact
                        ? hideText
                          ? "hidden" // Always hide text in canvas mode (container is narrow)
                          : "hidden sm:inline text-sm max-w-[160px] xs:max-w-[190px] sm:max-w-[210px]"
                        : "text-sm"
                    )}
                  >
                    {currentAssistant?.name || "Select an assistant..."}
                  </span>
                  <motion.div
                    initial={false}
                    animate={{
                      rotate: open ? 360 : 0,
                      scale: open ? 1.1 : 1,
                    }}
                    className="text-primary/70 flex-shrink-0"
                  >
                    <AssistantAvatar
                      assistant={currentAssistant}
                      className="h-[22px] w-[22px]"
                    />
                  </motion.div>
                </>
              )}
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "p-0 bg-white/70 dark:bg-zinc-900/65 border border-gray-200/70 dark:border-zinc-700/70 backdrop-blur-md shadow-lg shadow-black/10 z-[400] rounded-2xl outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 ring-offset-0 focus-visible:ring-offset-0",
            isInlineEditing || isInlineCreating
              ? "w-[95vw] max-w-[350px] xs:w-[350px] sm:w-[420px]"
              : "w-[82vw] max-w-[320px] xs:w-[270px] sm:w-[300px]"
          )}
          align="start"
          sideOffset={4}
          style={{ zIndex: 400 }}
          forceMount
          disableFocusStyles
        >
          {isInlineCreating ? (
            <div className="max-h-[80vh] overflow-hidden rounded-2xl bg-transparent">
              {/* Inline Create Form - no header, closes by clicking outside */}
              <div className="max-h-[80vh] overflow-auto">
                <AssistantCreationFlow
                  assistant={creatingAssistant}
                  onSavingStateChange={(
                    saving,
                    hasChanges,
                    canSaveNow,
                    callback
                  ) => {
                    setInlineIsSaving(saving);
                    setInlineHasUnsavedChanges(hasChanges);
                    setInlineCanSave(canSaveNow);
                    if (callback) {
                      setInlineSaveCallback(() => callback);
                    }
                  }}
                  onCancel={handleCloseInlineCreate}
                  onStreamingUpdate={handleStreamingUpdate}
                  onCreationComplete={(updatedAssistant) => {
                    // Switch from creating mode to editing mode (non-streaming path)
                    setIsInlineCreating(false);
                    setCreatingAssistant(null);
                    setEditingAssistant(updatedAssistant);
                    setIsInlineEditing(true);
                    // Reset saving state for the edit form
                    setInlineIsSaving(false);
                    setInlineHasUnsavedChanges(false);
                    setInlineCanSave(false);
                    setInlineSaveCallback(null);
                    setIsStreaming(false);
                    setStreamingFields(null);
                  }}
                />
              </div>
            </div>
          ) : isInlineEditing ? (
            <div className="max-h-[80vh] overflow-hidden rounded-2xl bg-transparent">
              {/* Inline Edit Header */}
              <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCloseInlineEdit}
                  className="h-8 w-8 p-0 flex items-center justify-center hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveInlineEdit}
                  disabled={
                    inlineIsSaving || !inlineCanSave || !inlineSaveCallback
                  }
                  className="h-8 px-4 flex items-center gap-1.5 bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90 rounded-3xl text-sm font-medium"
                >
                  {inlineIsSaving && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  )}
                  <span>Save</span>
                </Button>
              </div>
              {/* Inline Edit Form */}
              <div className="max-h-[calc(80vh-60px)] overflow-auto px-4 py-3">
                {editingAssistant && (
                  <AssistantForm
                    assistant={editingAssistant}
                    onSavingStateChange={(
                      saving,
                      hasChanges,
                      canSaveNow,
                      callback
                    ) => {
                      setInlineIsSaving(saving);
                      setInlineHasUnsavedChanges(hasChanges);
                      setInlineCanSave(canSaveNow);
                      if (callback) {
                        setInlineSaveCallback(() => callback);
                      }
                    }}
                    hideInstructionsGenerator={false}
                    streamingFields={streamingFields}
                    isStreaming={isStreaming}
                  />
                )}
              </div>
            </div>
          ) : (
            <Command className="max-h-[400px] overflow-hidden rounded-2xl bg-transparent outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 ring-offset-0 focus-visible:ring-offset-0">
              {/* Show error message with retry option */}
              {(error || generationTimeout) &&
              (!assistants || assistants.length === 0) ? (
                <div className="p-4 text-center">
                  <div
                    className={cn(
                      "mb-3",
                      error
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-red-500 dark:text-red-400"
                    )}
                  >
                    <svg
                      className="w-8 h-8 mx-auto mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1 font-medium">
                    {error ? "Connection Error" : "Setup Incomplete"}
                  </p>
                  <p className="text-xs text-muted-foreground/70 mb-3">
                    {error ||
                      "Assistant generation timed out. Please try again or create one manually."}
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={() => {
                        setGenerationTimeout(false);
                        initializeAssistants();
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Retry
                    </Button>
                    <Button onClick={() => handleSelect("add-new")} size="sm">
                      Create Assistant
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                    No assistants found.
                  </CommandEmpty>
                  <div
                    className="scrollbar-thin max-h-[280px] overflow-y-auto"
                    data-testid="assistant-list"
                  >
                    <CommandGroup className="">
                      <AnimatePresence mode="popLayout">
                        {filteredAssistants.map((assistant, index) => (
                          <motion.div
                            key={assistant.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{
                              opacity: 1,
                              y: 0,
                              transition: { delay: index * 0.03 },
                            }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <CommandItem
                              value={assistant.id}
                              onSelect={() => handleSelect(assistant.id)}
                              data-testid="assistant-item"
                              className={cn(
                                "flex items-center gap-2 px-2 py-2 mx-0 rounded-xl",
                                "cursor-pointer",
                                "hover:bg-white/20 dark:hover:bg-white/10",
                                currentAssistant?.id === assistant?.id
                                  ? "bg-white/30 dark:bg-white/15"
                                  : "hover:shadow-sm",
                                "transition-all duration-200",
                                "group",
                                "outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 ring-offset-0 focus-visible:ring-offset-0"
                              )}
                            >
                              <motion.div
                                initial={false}
                                animate={{
                                  scale:
                                    currentAssistant?.id === assistant?.id
                                      ? 1.1
                                      : 1,
                                }}
                                className="flex-shrink-0"
                              >
                                <AssistantAvatar
                                  assistant={assistant}
                                  className="h-6 w-6"
                                />
                              </motion.div>
                              <div className="flex flex-col flex-1 min-w-0">
                                <span className="flex items-center truncate text-sm">
                                  {assistant.name}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEdit(e, assistant.id);
                                    return false;
                                  }}
                                  className={cn(
                                    "opacity-0 group-hover:opacity-100",
                                    "flex h-6 w-6 items-center justify-center",
                                    "rounded-md",
                                    "bg-transparent",
                                    "border-2 border-gray-500/[0.15]",
                                    "hover:bg-transparent",
                                    "hover:border-gray-500/50",
                                    "transition-all duration-200",
                                    "focus:outline-none",
                                    "focus:ring-2",
                                    "focus:ring-gray-500/30",
                                    "flex-shrink-0",
                                    "focus:border-gray-500/50",
                                    "relative"
                                  )}
                                >
                                  <div className="absolute inset-0 rounded-md opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none blur-[1px] bg-gradient-to-r from-white/[0.02] via-white/[0.03] to-white/[0.02]" />
                                  <Edit className="h-3 w-3 text-white/80 dark:text-white/90" />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Check if there are other assistants available
                                    const otherAssistants = assistants.filter(
                                      (a) => a.id !== assistant.id
                                    );
                                    if (otherAssistants.length === 0) {
                                      alert(
                                        "Cannot delete the last assistant. You must have at least one assistant."
                                      );
                                      return false;
                                    }
                                    // Don't allow deleting the currently selected assistant
                                    if (currentAssistant?.id === assistant.id) {
                                      handleDelete(e, assistant.id);
                                      return false;
                                    }
                                    handleDelete(e, assistant.id);
                                    return false;
                                  }}
                                  className={cn(
                                    "opacity-0 group-hover:opacity-100",
                                    "flex h-6 w-6 items-center justify-center",
                                    "rounded-md",
                                    "bg-transparent",
                                    "border-2 border-red-500/[0.15]",
                                    "hover:bg-transparent",
                                    "hover:border-red-500/50",
                                    "transition-all duration-200",
                                    "focus:outline-none",
                                    "focus:ring-2",
                                    "focus:ring-red-500/30",
                                    "flex-shrink-0",
                                    "relative"
                                  )}
                                >
                                  <div className="absolute inset-0 rounded-md opacity-0 transition-opacity duration-300 group-hover:opacity-100 pointer-events-none blur-[1px] bg-gradient-to-r from-white/[0.02] via-white/[0.03] to-white/[0.02]" />
                                  <Trash2 className="h-3 w-3 text-red-500" />
                                </motion.button>
                              </div>
                            </CommandItem>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </CommandGroup>
                  </div>
                </>
              )}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <CommandGroup className="pt-0 bg-transparent">
                  <div className="border-t border-border/50 mt-1 pt-1 px-1">
                    <div className="grid grid-cols-2 gap-1">
                      <CommandItem
                        value="add-new"
                        onSelect={() => handleSelect("add-new")}
                        data-testid="assistant-create-button"
                        className={cn(
                          "flex items-center justify-center px-1 py-1 rounded-lg",
                          "cursor-pointer",
                          "hover:bg-white/20 dark:hover:bg-white/10",
                          "transition-colors duration-200",
                          "outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 ring-offset-0 focus-visible:ring-offset-0"
                        )}
                      >
                        <span className="text-xs font-medium">Create</span>
                      </CommandItem>
                      <CommandItem
                        value="search-assistants"
                        onSelect={() => {
                          setOpen(false);
                          setSearchDialogOpen(true);
                        }}
                        className={cn(
                          "flex items-center justify-center px-1 py-1 rounded-lg",
                          "cursor-pointer",
                          "hover:bg-white/20 dark:hover:bg-white/10",
                          "transition-colors duration-200",
                          "outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 ring-offset-0 focus-visible:ring-offset-0"
                        )}
                      >
                        <span className="text-xs font-medium">Search</span>
                      </CommandItem>
                    </div>
                  </div>
                </CommandGroup>
              </motion.div>
            </Command>
          )}
        </PopoverContent>
      </Popover>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogPortal>
          <AlertDialogOverlay
            className="fixed inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            style={{ zIndex: 99999 }}
          />
          <AlertDialogContent
            className="sm:max-w-[425px] relative fixed left-[50%] top-[50%] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
            style={{ zIndex: 100000 }}
          >
            {/* X Close Button */}
            <button
              onClick={handleCancelDelete}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            >
              <X className="h-4 w-4 text-red-500 border border-red-500 rounded p-0.5" />
              <span className="sr-only">Close</span>
            </button>

            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                  <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                Delete Assistant
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-foreground">
                  {assistantToDelete?.name}
                </span>
                ? This action cannot be undone and will permanently remove this
                assistant and all its configurations.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex gap-2">
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
              >
                Delete Assistant
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>

      {/* Browse Assistants Dialog */}
      <AssistantSearchDialog
        isOpen={searchDialogOpen}
        onOpenChange={setSearchDialogOpen}
        onAssistantSelect={(assistant) => {
          setCurrentAssistant(assistant);
          if (onAssistantSelect) {
            onAssistantSelect(assistant);
          }
        }}
      />
    </>
  );
}

AssistantSelector.propTypes = {
  className: PropTypes.string,
  onAssistantSelect: PropTypes.func,
  compact: PropTypes.bool,
  hideText: PropTypes.bool,
};

export default AssistantSelector;
